import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Drawer,
  Typography,
  Skeleton,
  Empty,
  Button,
  Space,
  Tooltip,
  message,
  Modal,
} from 'antd';
import {
  RobotOutlined,
  PushpinOutlined,
  DeleteOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import { AgentPickerTrigger } from '@/components/AgentPickerDrawer';
import type { PrebuiltPrompt } from '@/api/types';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { StatusPill } from '@/components/StatusPill';

const { Text, Paragraph } = Typography;

interface PromptAgentLinkageDrawerProps {
  open: boolean;
  prompt: PrebuiltPrompt | null;
  onClose: () => void;
}

// Drawer that shows "which agents pin this prompt?" and lets the operator
// pin/unpin in one click. Reads from PrebuiltPromptStore.agentsForPrompt and
// the existing AgentRegistryStore for the candidate list.

export const PromptAgentLinkageDrawer = observer(function PromptAgentLinkageDrawer({
  open,
  prompt,
  onClose,
}: PromptAgentLinkageDrawerProps) {
  const { prebuiltPromptStore: store, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [picking, setPicking] = useState<number | undefined>();

  useEffect(() => {
    if (open && prompt) {
      void store.loadAgentsForPrompt(prompt.id);
    }
    return () => {
      if (!open) store.clearAgentsForPrompt();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prompt?.id]);

  const linked = store.agentsForPrompt;

  // Candidates: the practice's agents not yet pinned to this prompt, listed by the server a page at a time
  // (2 Nov 2026; this drawer used to offer the first 100 of the practice's agents from a store).
  const pinnedIds = useMemo(() => linked.map((l) => l.agentId), [linked]);

  async function handlePin(): Promise<void> {
    if (!prompt || picking == null) return;
    try {
      await store.pinToAgent(prompt.id, picking);
      message.success('Prompt pinned');
      setPicking(undefined);
      trackEvent('Prompt.PinnedToAgent', { promptId: prompt.id, agentId: picking });
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to pin');
    }
  }

  async function handleUnpin(linkRowId: number, agentId: number): Promise<void> {
    if (!prompt) return;
    Modal.confirm({
      title: 'Unpin from this agent?',
      content: 'The chip will no longer appear above the chat for this agent.',
      okText: 'Unpin',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await store.unpinFromAgent(linkRowId, prompt.id);
          message.success('Unpinned');
          trackEvent('Prompt.UnpinnedFromAgent', { promptId: prompt.id, agentId });
        } catch (err) {
          message.error(err instanceof Error ? err.message : 'Failed to unpin');
        }
      },
    });
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={520}
      title={
        <Space>
          <PushpinOutlined />
          <span>Pinned agents</span>
        </Space>
      }
      destroyOnClose
    >
      {prompt && (
        <div
          style={{
            background: isDark ? colors.slate[700] : colors.ink[50],
            border: `1px solid ${isDark ? colors.slate[600] : colors.ink[100]}`,
            borderRadius: 10,
            padding: '12px 14px',
            marginBottom: 14,
          }}
        >
          <Text
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: isDark ? colors.slate[400] : colors.slate[500],
            }}
          >
            Prompt
          </Text>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: isDark ? colors.slate[100] : colors.slate[900],
              marginTop: 2,
            }}
          >
            {prompt.description}
          </div>
          {prompt.buttonText && (
            <div
              style={{
                fontSize: 12,
                fontFamily: "'JetBrains Mono', monospace",
                color: isDark ? colors.slate[400] : colors.slate[500],
                marginTop: 4,
              }}
            >
              "{prompt.buttonText}"
            </div>
          )}
        </div>
      )}

      <Paragraph type="secondary" style={{ fontSize: 12.5, marginBottom: 14 }}>
        When a prompt is pinned to an agent, it shows up as a one-click chip above that agent's
        chat — the user clicks it, and the prompt's instructions get sent to the agent.
      </Paragraph>

      {/* Pin to a new agent */}
      <div
        style={{
          background: isDark ? colors.slate[800] : '#FFFFFF',
          border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          borderRadius: 10,
          padding: '12px 14px',
          marginBottom: 14,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: isDark ? colors.slate[400] : colors.slate[500],
            marginBottom: 8,
          }}
        >
          Pin to an agent
        </div>
        <Space.Compact style={{ width: '100%' }}>
          <AgentPickerTrigger
            value={picking ?? null}
            onChange={(id) => setPicking(id ?? undefined)}
            placeholder="Select an agent…"
            drawerTitle="Pin this prompt to an agent"
            excludeIds={pinnedIds}
            allowClear={false}
            style={{ flex: 1 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handlePin}
            disabled={picking == null}
          >
            Pin
          </Button>
        </Space.Compact>
      </div>

      {/* Currently pinned list */}
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: isDark ? colors.slate[400] : colors.slate[500],
          marginBottom: 8,
        }}
      >
        Currently pinned ({linked.length})
      </div>

      {store.isLoadingAgentsForPrompt ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : linked.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ fontSize: 13, color: isDark ? colors.slate[400] : colors.slate[500] }}>
              Not pinned to any agent yet. Pin it above to surface it as a chat chip.
            </span>
          }
        />
      ) : (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {linked.map((lk) => (
            <div
              key={lk.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 14px',
                background: isDark ? colors.slate[800] : '#FFFFFF',
                border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: isDark ? `${colors.ink[700]}55` : colors.ink[50],
                  color: colors.ink[600],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  flexShrink: 0,
                }}
              >
                <RobotOutlined />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: isDark ? colors.slate[100] : colors.slate[900],
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {lk.displayName ?? lk.a2AAgentName ?? `Agent #${lk.agentId}`}
                </div>
                {lk.a2AAgentName && lk.displayName && (
                  <div
                    style={{
                      fontSize: 11,
                      fontFamily: "'JetBrains Mono', monospace",
                      color: isDark ? colors.slate[400] : colors.slate[500],
                      marginTop: 1,
                    }}
                  >
                    {lk.a2AAgentName}
                  </div>
                )}
              </div>
              <StatusPill tone="ink" icon={<ThunderboltOutlined />}>
                #{lk.displayOrder}
              </StatusPill>
              <Tooltip title="Unpin from this agent">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleUnpin(lk.id, lk.agentId)}
                />
              </Tooltip>
            </div>
          ))}
        </Space>
      )}
    </Drawer>
  );
});
